import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, EditOutlined, DeleteOutlined, Search } from '@mui/icons-material';
import { areaService } from '../../areas';
import AreaModalForm from '../../areas/components/AreaModalForm';
import AreaDetailModal from '../../areas/components/AreaDetailModal';
import { companyService } from '../index';
import { usePermissions } from '../../../hooks/usePermissions';
import useDebounce from '../../../hooks/useDebounce';

const CompanyAreas = ({ companyId }) => {
  const { hasPermission } = usePermissions();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companies, setCompanies] = useState([]);
  const [detailAreaId, setDetailAreaId] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  useEffect(() => {
    loadCompanies();
  }, [companyId]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    loadAreas();
  }, [companyId, debouncedSearch, page]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadAreas = async () => {
    try {
      setLoading(true);
      const response = await areaService.getAll({ companyId, limit: 10, page, search: debouncedSearch || undefined });
      setAreas(response.data);
      setPagination(response.pagination || { total: 0, totalPages: 1 });
    } catch (error) {
      showSnackbar('Error al cargar áreas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedArea(null);
    setOpenModal(true);
  };

  const handleEdit = (area) => {
    setSelectedArea(area);
    setOpenModal(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedArea) {
        await areaService.update(selectedArea.id, data);
        showSnackbar('Área actualizada exitosamente', 'success');
      } else {
        await areaService.create({ ...data, companyId: parseInt(companyId) });
        showSnackbar('Área creada exitosamente', 'success');
      }
      setOpenModal(false);
      loadAreas();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar área', 'error');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta área?')) {
      try {
        await areaService.delete(id);
        showSnackbar('Área eliminada exitosamente', 'success');
        loadAreas();
      } catch (error) {
        showSnackbar('Error al eliminar área', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Áreas de la Empresa</Typography>
        {hasPermission('area.create') && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Área
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre o código..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : areas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>{search ? 'No se encontraron áreas con esa búsqueda' : 'No hay áreas registradas'}</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Codigo</TableCell>
                  <TableCell>Usuarios Asignados</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {areas.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((area) => (
                  <TableRow
                    key={area.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDetailAreaId(area.id)}
                  >
                    <TableCell
                      sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 600 }}
                    >
                      {area.name}
                    </TableCell>
                    <TableCell>{area.code}</TableCell>
                    <TableCell>{area._count?.areaUsers || 0}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'inline-flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                        {hasPermission('area.update') && (
                          <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={() => handleEdit(area)}>
                            <EditOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                        {hasPermission('area.delete') && (
                          <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={() => handleDelete(area.id)}>
                            <DeleteOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(_, val) => setPage(val)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <AreaDetailModal
        open={!!detailAreaId}
        areaId={detailAreaId}
        onClose={() => setDetailAreaId(null)}
      />

      <AreaModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        area={selectedArea}
        companies={companies}
        preselectedCompanyId={companyId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyAreas;
